// ============================================================================
// Site Configuration Service
// Manages store-level site types and sections
// ============================================================================

import {
  StoreSiteType,
  StoreSiteTypeCreate,
  StoreSiteTypeUpdate,
  StoreSiteSection,
  StoreSiteSectionCreate,
  StoreSiteSectionUpdate,
} from '../types/site';
import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Site Types
// ============================================================================

export async function getStoreSiteTypes(storeId?: number): Promise<StoreSiteType[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/store-site-types/${queryString ? '?' + queryString : ''}`);
}

export async function createStoreSiteType(data: StoreSiteTypeCreate, storeId?: number): Promise<StoreSiteType> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/store-site-types/${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStoreSiteType(id: number, data: StoreSiteTypeUpdate): Promise<StoreSiteType> {
  return fetchWithAuth(`${API_BASE_URL}/store-site-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStoreSiteType(id: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/store-site-types/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Site Sections
// ============================================================================

export async function getStoreSiteSections(storeId?: number): Promise<StoreSiteSection[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  return fetchWithAuth(`${API_BASE_URL}/store-site-sections?${params}`);
}

export async function createStoreSiteSection(data: StoreSiteSectionCreate, storeId?: number): Promise<StoreSiteSection> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  return fetchWithAuth(`${API_BASE_URL}/store-site-sections?${params}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStoreSiteSection(id: number, data: StoreSiteSectionUpdate): Promise<StoreSiteSection> {
  return fetchWithAuth(`${API_BASE_URL}/store-site-sections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStoreSiteSection(id: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/store-site-sections/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Named Export Object
// ============================================================================

export const siteConfigService = {
  getStoreSiteTypes,
  createStoreSiteType,
  updateStoreSiteType,
  deleteStoreSiteType,
  getStoreSiteSections,
  createStoreSiteSection,
  updateStoreSiteSection,
  deleteStoreSiteSection,
};

export default siteConfigService;
